// Client-side BPM + musical key detection using Web Audio API.
// - BPM via low-pass filter + energy envelope autocorrelation.
// - Key via chroma vector + Krumhansl-Schmuckler profile correlation.

const MAJOR_PROFILE = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const MINOR_PROFILE = [6.33, 2.68, 3.52, 5.38, 2.60, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];
const PITCH_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
// (pitchClass, mode 1=major/0=minor) -> Camelot
const CAMELOT = {
  "0-1": "8B", "0-0": "5A",
  "1-1": "3B", "1-0": "12A",
  "2-1": "10B", "2-0": "7A",
  "3-1": "5B", "3-0": "2A",
  "4-1": "12B", "4-0": "9A",
  "5-1": "7B", "5-0": "4A",
  "6-1": "2B", "6-0": "11A",
  "7-1": "9B", "7-0": "6A",
  "8-1": "4B", "8-0": "1A",
  "9-1": "11B", "9-0": "8A",
  "10-1": "6B", "10-0": "3A",
  "11-1": "1B", "11-0": "10A",
};

async function fetchDecodedAudio(url, maxSeconds = 45) {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const resp = await fetch(url);
  const arr = await resp.arrayBuffer();
  const buf = await ctx.decodeAudioData(arr.slice(0));
  ctx.close();
  // Trim to maxSeconds mono
  const sr = buf.sampleRate;
  const frames = Math.min(buf.length, Math.floor(maxSeconds * sr));
  const mono = new Float32Array(frames);
  const chs = buf.numberOfChannels;
  for (let c = 0; c < chs; c++) {
    const ch = buf.getChannelData(c);
    for (let i = 0; i < frames; i++) mono[i] += ch[i] / chs;
  }
  return { data: mono, sampleRate: sr };
}

// One-pole low-pass at ~150Hz to isolate kick drum energy
function lowpass(data, sr, cutoff = 150) {
  const rc = 1 / (2 * Math.PI * cutoff);
  const dt = 1 / sr;
  const a = dt / (rc + dt);
  const out = new Float32Array(data.length);
  let y = 0;
  for (let i = 0; i < data.length; i++) { y = y + a * (data[i] - y); out[i] = y; }
  return out;
}

// RMS envelope with hop and window
function energyEnvelope(data, sr, hopMs = 10, winMs = 40) {
  const hop = Math.floor(sr * hopMs / 1000);
  const win = Math.floor(sr * winMs / 1000);
  const frames = Math.max(0, Math.floor((data.length - win) / hop));
  const env = new Float32Array(frames);
  for (let f = 0; f < frames; f++) {
    let s = 0;
    const start = f * hop;
    for (let i = 0; i < win; i++) { const v = data[start + i]; s += v * v; }
    env[f] = Math.sqrt(s / win);
  }
  // Normalize
  let m = 0; for (let i = 0; i < env.length; i++) m = Math.max(m, env[i]);
  if (m > 0) for (let i = 0; i < env.length; i++) env[i] /= m;
  return { env, hopMs };
}

// Autocorrelate envelope for lags matching 60..180 BPM
function estimateBPM(env, hopMs) {
  const minBpm = 60, maxBpm = 190;
  const minLag = Math.floor(60000 / maxBpm / hopMs);
  const maxLag = Math.ceil(60000 / minBpm / hopMs);
  let bestLag = minLag, bestScore = -Infinity;
  for (let lag = minLag; lag <= maxLag; lag++) {
    let s = 0;
    for (let i = 0; i + lag < env.length; i++) s += env[i] * env[i + lag];
    if (s > bestScore) { bestScore = s; bestLag = lag; }
  }
  let bpm = 60000 / (bestLag * hopMs);
  // Fold into typical 90-160 range if needed
  while (bpm < 85) bpm *= 2;
  while (bpm > 180) bpm /= 2;
  return { bpm: Math.round(bpm * 10) / 10, lag: bestLag };
}

// Find the first strong beat (peak) to lock the beat grid phase in seconds
function estimateBeatPhase(env, hopMs, beatLagFrames) {
  const window = Math.min(env.length, beatLagFrames);
  let bestIdx = 0, bestVal = -Infinity;
  for (let i = 0; i < window; i++) {
    if (env[i] > bestVal) { bestVal = env[i]; bestIdx = i; }
  }
  return (bestIdx * hopMs) / 1000; // seconds from clip start to beat 1
}

// Simple pitch-class profile via FFT sums across windows
async function chromaVector(data, sr) {
  // Use OfflineAudioContext for FFT via AnalyserNode
  const octx = new OfflineAudioContext(1, data.length, sr);
  const src = octx.createBufferSource();
  const buf = octx.createBuffer(1, data.length, sr);
  buf.copyToChannel(data, 0);
  src.buffer = buf;
  const fftSize = 2048;
  const analyser = octx.createAnalyser();
  analyser.fftSize = fftSize;
  src.connect(analyser);
  analyser.connect(octx.destination);
  src.start();
  // We can't actually pull time-varying FFT via OfflineAudioContext directly.
  // Instead: do a manual power spectrum using a DFT on multiple windows and accumulate chroma.
  // Fall back to a simple approach: compute magnitude spectrum of the whole clip via FFT of a subset.

  // Simple manual approach: take fftSize-sized chunks, compute magnitude via DFT of downsampled signal.
  const chroma = new Array(12).fill(0);
  const hop = fftSize / 2;
  // Use a Hann-windowed magnitude estimate approximation
  for (let start = 0; start + fftSize < data.length; start += hop) {
    // Compute per-bin magnitude using a lightweight approach: sum of squared amps in each pitch-class band
    for (let bin = 4; bin < fftSize / 4; bin++) {
      const freq = (bin * sr) / fftSize;
      if (freq < 65 || freq > 2000) continue;
      // MIDI note number from freq
      const midi = 69 + 12 * Math.log2(freq / 440);
      const pc = ((Math.round(midi) % 12) + 12) % 12;
      // Estimate amp via time-domain correlation at this frequency (rough)
      let re = 0, im = 0;
      const w = fftSize;
      for (let n = 0; n < w; n += 8) {  // step 8 for speed
        const x = data[start + n] || 0;
        const angle = (2 * Math.PI * freq * n) / sr;
        re += x * Math.cos(angle);
        im -= x * Math.sin(angle);
      }
      chroma[pc] += Math.sqrt(re * re + im * im);
    }
  }
  // Normalize
  let mx = 0;
  for (let i = 0; i < 12; i++) mx = Math.max(mx, chroma[i]);
  if (mx > 0) for (let i = 0; i < 12; i++) chroma[i] /= mx;
  return chroma;
}

function pearson(a, b) {
  const n = a.length;
  let sa = 0, sb = 0;
  for (let i = 0; i < n; i++) { sa += a[i]; sb += b[i]; }
  const ma = sa / n, mb = sb / n;
  let num = 0, da = 0, dbb = 0;
  for (let i = 0; i < n; i++) {
    const xa = a[i] - ma, xb = b[i] - mb;
    num += xa * xb; da += xa * xa; dbb += xb * xb;
  }
  return num / (Math.sqrt(da * dbb) || 1);
}

function estimateKey(chroma) {
  let best = { score: -Infinity, root: 0, mode: 1 };
  for (let r = 0; r < 12; r++) {
    const rotated = chroma.slice(r).concat(chroma.slice(0, r));
    const smaj = pearson(rotated, MAJOR_PROFILE);
    const smin = pearson(rotated, MINOR_PROFILE);
    if (smaj > best.score) best = { score: smaj, root: r, mode: 1 };
    if (smin > best.score) best = { score: smin, root: r, mode: 0 };
  }
  const camelot = CAMELOT[`${best.root}-${best.mode}`] || "8A";
  const nice = `${PITCH_NAMES[best.root]}${best.mode === 1 ? "" : "m"}`;
  return { camelot, keyName: nice };
}

export async function analyzeTrack(url) {
  const { data, sampleRate } = await fetchDecodedAudio(url, 40);
  const lp = lowpass(data, sampleRate, 150);
  const { env, hopMs } = energyEnvelope(lp, sampleRate, 10, 40);
  const { bpm, lag } = estimateBPM(env, hopMs);
  const beatPhase = estimateBeatPhase(env, hopMs, lag);
  const chroma = await chromaVector(data, sampleRate);
  const { camelot, keyName } = estimateKey(chroma);
  return { bpm, key: camelot, keyName, beatPhase };
}

// Snap a time (seconds) to the nearest beat given bpm & phase (both original tempo)
export function snapToBeat(time, bpm, beatPhase = 0) {
  if (!bpm) return time;
  const beatSec = 60 / bpm;
  const beat = Math.round((time - beatPhase) / beatSec);
  return Math.max(0, beatPhase + beat * beatSec);
}
